import React, { useCallback, useState } from 'react'
import {
  Div,
  Flex,
  QuantumThemeWrapper,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import {
  GlassBackgroundContainer,
  GlassBlobType,
} from 'src/pricing_page/components/GlassBackground'
import { BannerSlider } from 'src/home_page/components/BannerSlider/BannerSlider'
import { Button } from 'src/design_system/components/Button'
import { CardStoriesRibbon } from 'src/home_page/components/CardStoriesRibbon/CardStoriesRibbon'

import { HomePageBanner } from 'src/home_page/components/HomePageBanner/HomePageBanner'
import { useWindowWidth } from '@react-hook/window-size'
import { performOperation } from 'src/app/api'
import {
  CreateSparseUser,
  CreateSparseUserData,
  InviteFragment,
} from 'src/graphql'
import { Transition } from 'src/chrome'
import { openApp } from 'src/helpers/appHelpers'
import useSelector from 'src/hooks/useSelector'
import { CustomerReview } from 'src/home_page/components/ConsumerReviews/components/CustomerReview'
import { useHomePage } from 'src/strapi/hooks/useHomePage'
import { CardTypeSection } from '../components/CardTypeSection'
import { ImageDiffFragment, SlideType } from 'src/strapi/queries'
import { VideoPlayerSection } from '../components/VideoPlayerSection'
import { normalizeVideo } from 'src/strapi/helpers/normalizeVideo'
import { useCtaExecutor } from 'src/strapi/hooks/useCtaExecutor'

type LaunchPageType = { invite?: InviteFragment }

const blobs: GlassBlobType[] = [
  {
    size: '30%',
    shape: 'circle',
    filter: 'blur(100px)',
    offset: {
      top: '10%',
      left: '-35%',
    },
    // Pink Gradient
    gradient:
      'linear-gradient(to bottom right,  #EC4899 100%, #D946EE 100%) bottom left / 50% 50% no-repeat',
  },
  {
    size: '40%',
    shape: 'circle',
    filter: 'blur(100px)',
    offset: {
      top: '-30%',
      left: '-10%',
    },
    // Orange Gradient
    gradient:
      'linear-gradient(to bottom left, #EC4898 100%, #F59E10 100%) bottom left / 50% 50% no-repeat',
  },
  {
    size: '40%',
    shape: 'circle',
    filter: 'blur(70px)',
    offset: {
      top: '20%',
      left: '-10%',
    },
    // Yellow
    gradient:
      'linear-gradient(to bottom left, #FDE588 100%, #FACC1B 100%) bottom left / 50% 50% no-repeat',
  },
  {
    size: '40%',
    shape: 'circle',
    filter: 'blur(50px)',
    offset: {
      top: '5%',
      left: '40%',
    },
    // Orange
    gradient:
      'linear-gradient(to bottom left, #EC4898 100%, #F59E10 100%) bottom left / 50% 50% no-repeat',
  },
  {
    size: '40%',
    shape: 'circle',
    filter: 'blur(70px)',
    offset: {
      top: '-35%',
      left: '30%',
    },
    // Yellow
    gradient:
      'linear-gradient(to bottom left, #FDE588 100%, #FACC1B 100%) bottom left / 50% 50% no-repeat',
  },
  {
    size: '40%',
    shape: 'circle',
    filter: 'blur(50px)',
    offset: {
      top: '5%',
      left: '40%',
    },
    // Lavender
    gradient:
      'linear-gradient(to bottom left, #DDD6FD 100%, #FACFE8 100%) bottom left / 50% 50% no-repeat',
  },
]

// tslint:disable-next-line: cyclomatic-complexity score 25
const TestLaunchPage: React.FC<LaunchPageType> = props => {
  const { invite } = props

  const state = useSelector(state => state)
  const windowWidth = useWindowWidth()
  const isSmallMobile = windowWidth <= 500
  const isMobile = windowWidth <= 1180
  const [activeCardTypeBentoId, setActiveCardTypeBentoId] = useState(0)
  const [isCreatingAccount, setIsCreatingAccount] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const sponsorId = state.storefront.storefront?.user.genealogyId.toString()
  const open = (href: string) => () => {
    window.location.href = href
  }
  const goToApp = useCallback((query: { [key: string]: string } = {}) => {
    setIsRedirecting(true)
    openApp('', query)
  }, [])

  const handleSendFreeCard = useCallback(() => {
    if (invite) {
      setIsCreatingAccount(true)
      performOperation<CreateSparseUserData>(
        CreateSparseUser({
          inviteId: invite.id,
        }),
        result => {
          setIsCreatingAccount(false)
          if (result.value) {
            goToApp({
              access_token: result.value.createSparseUser.account.token,
              postcardPromo: '',
            })
          } else {
            goToApp({
              verify: '',
              postcardPromo: '',
            })
          }
        },
      )
    } else {
      goToApp({
        verify: '',
        postcardPromo: '',
      })
    }
  }, [invite?.id, goToApp]) // eslint-disable-line

  const { data, loading } = useHomePage(handleSendFreeCard)
  const rawSlides =
    data.slides && 'slides' in data.slides
      ? data.slides.slides ?? []
      : Array.isArray(data.slides)
      ? data.slides
      : []

  const slides = rawSlides.filter(Boolean) as SlideType[]
  const storyCards = data.stories
  const videoSection = data.videoBlock || []
  const executeCta = useCtaExecutor({ handleSendFreeCard, sponsorId })
  const primaryVideo = normalizeVideo(videoSection[0]) || null
  const secondaryVideo = normalizeVideo(videoSection[1]) || null

  if (isCreatingAccount || isRedirecting || loading) {
    return (
      <Transition
        message={
          isRedirecting || loading ? 'Loading...' : 'Creating guest account...'
        }
        messageStyle={{
          color: 'black',
          fontWeight: 500,
        }}
      />
    )
  }

  return (
    <QuantumThemeWrapper>
      <Flex
        flexDirection="column"
        alignItems="center"
        backgroundColor="rgb(249, 250, 251)"
        position="relative"
        rowGap="x4"
        width="100%"
        overflowX="hidden"
      >
        <Div
          position={'absolute'}
          style={{
            background:
              'linear-gradient(180deg, #FFFFFF 0%, #F7F2F9 50%, #EBE5F0 100%)',
            backdropFilter: 'blur(200px)',
          }}
        >
          <HomePageBanner />
        </Div>
        <Spacer orientation="vertical" space="x4" />
        <BannerSlider
          slides={slides}
          openApp={openApp}
          handleSendFreeCard={handleSendFreeCard}
        />
        <CardStoriesRibbon stories={storyCards} />
        <Flex
          id="bento_sections_container"
          flexDirection="column"
          inset={{
            horizontal:
              windowWidth <= 530 ? 'x2' : windowWidth <= 800 ? 'x4' : 'x5',
          }}
          position="relative"
          rowGap="x4"
          width="100%"
        >
          <Flex
            flexDirection="column"
            width="100%"
            height="100%"
            inset={{
              bottom: 'x4',
            }}
          >
            {data.bentoSections.map((section, index) => {
              const images = (section.imageDiffs ?? []).filter(
                (img: any): img is ImageDiffFragment => img != null,
              )
              return (
                <CardTypeSection
                  key={index}
                  isMobile={isMobile}
                  activeImageIndex={activeCardTypeBentoId}
                  setActiveImageIndex={(id: number) =>
                    setActiveCardTypeBentoId(id)
                  }
                  images={images}
                  cardSection={section}
                />
              )
            })}
          </Flex>
        </Flex>
        <Flex width="100%" justifyContent="center" inset="x4">
          <Button
            title="View our packages"
            backgroundColor={'#fff'}
            textColor={'#404040'}
            onClick={open(`/app/pricing/`)}
            padding="1rem 1.25rem"
          />
        </Flex>
        <GlassBackgroundContainer blobStart="center" blobs={blobs}>
          {primaryVideo?.ready && (
            <VideoPlayerSection
              title={primaryVideo?.title ?? ''}
              description={primaryVideo?.description ?? ''}
              thumbnailUrl={primaryVideo?.thumbnailUrl ?? ''}
              iFrameSrc={primaryVideo?.iframeSrc ?? ''}
              isSmallMobile={isSmallMobile}
              primaryAction={{
                title: primaryVideo?.cta?.label || 'Send a Card',
                onClick: primaryVideo?.cta
                  ? () => executeCta(primaryVideo.cta)
                  : () => {},
              }}
            />
          )}
        </GlassBackgroundContainer>
        <Flex
          flexDirection="column"
          justifyContent="center"
          width="100%"
          inset="2rem"
        >
          <Text
            type="body"
            alignment="center"
            content="A trusted household name - see what others are saying"
            style={{
              fontFamily: 'Montserrat',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: '#404040',
            }}
          />
          <Flex
            width="100%"
            inset={{
              vertical: 'x6',
            }}
          >
            <CustomerReview />
          </Flex>
        </Flex>
        {secondaryVideo?.ready && (
          <VideoPlayerSection
            title={secondaryVideo?.title ?? ''}
            description={secondaryVideo?.description ?? ''}
            isSmallMobile={isSmallMobile}
            isComponentRight={secondaryVideo?.isComponentRight ?? true}
            thumbnailUrl={secondaryVideo?.thumbnailUrl ?? ''}
            iFrameSrc={secondaryVideo?.iframeSrc ?? ''}
            primaryAction={{
              title: secondaryVideo?.cta?.label ?? 'Learn More',
              onClick: secondaryVideo?.cta
                ? () => executeCta(secondaryVideo.cta)
                : () => {},
            }}
          />
        )}
      </Flex>
    </QuantumThemeWrapper>
  )
}

export default TestLaunchPage
